/**
 * Financial Aid Help Page
 *
 * FAQs, policy summaries, and deadlines for financial aid.
 */
import { useMemo } from 'react';
import { IconCalendar, IconQuestionMark } from '@tabler/icons-react';
import { useSchoolPublicInfo } from '../../internal-owned/school-settings-v2/hooks/useSchoolPublicInfo';

export function HelpPage() {
  const {
    financialAidOnTimeDeadlineLabel,
    financialAidHardCutoffDeadlineLabel,
    lateFeeLabel,
    isLoading,
  } = useSchoolPublicInfo();

  const faqs = useMemo(() => ([
    {
      question: 'Who is eligible for financial aid?',
      answer:
        'All families with students in grades K-12 are eligible to apply for financial aid. Preschool students are not eligible for scholarship assistance.',
    },
    {
      question: 'What documents are required?',
      answer:
        'You will need to provide tax returns, W-2 forms, bank statements, investment account statements, and proof of housing costs. Business owners must also provide business financial statements.',
    },
    {
      question: 'When is the application deadline?',
      answer:
        `Applications submitted by ${financialAidOnTimeDeadlineLabel} receive priority consideration with no late fee. Applications submitted after ${financialAidOnTimeDeadlineLabel} and before ${financialAidHardCutoffDeadlineLabel} incur a ${lateFeeLabel} late fee. Applications after ${financialAidHardCutoffDeadlineLabel} are not accepted.`,
    },
    {
      question: 'How are scholarships calculated?',
      answer:
        'Scholarship amounts are determined based on demonstrated financial need, including income, assets, liabilities, and family circumstances. Awards are expressed as a percentage of tuition.',
    },
    {
      question: 'Can I appeal a decision?',
      answer:
        'Yes, you may submit an appeal within 14 days of receiving your decision. Appeals must include a written explanation of why you believe the decision should be reconsidered.',
    },
  ]), [financialAidHardCutoffDeadlineLabel, financialAidOnTimeDeadlineLabel, lateFeeLabel]);

  const deadlines = useMemo(() => ([
    { date: financialAidOnTimeDeadlineLabel, event: 'Priority deadline (no late fee)' },
    { date: financialAidHardCutoffDeadlineLabel, event: `Final deadline (${lateFeeLabel} late fee after on-time window)` },
    { date: '14 days after decision', event: 'Appeal window closes' },
  ]), [financialAidHardCutoffDeadlineLabel, financialAidOnTimeDeadlineLabel, lateFeeLabel]);

  return (
    <section data-testid="financial-aid-help-root" className="mx-auto w-full max-w-5xl px-4 py-4">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Financial Aid Help</h1>
        <p className="mt-2 text-sm text-slate-600">
          Frequently asked questions, important deadlines, and policy information.
        </p>
      </header>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-4 flex items-center text-lg font-semibold text-slate-900">
          <IconCalendar size={20} className="mr-2 inline-block" />
          Important Deadlines
        </h2>
        <div className="space-y-2">
          {deadlines.map((deadline) => (
            <div
              key={deadline.event}
              className="flex flex-col gap-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="font-semibold text-slate-900">{deadline.date}</p>
              <p className="text-sm text-slate-600">{deadline.event}</p>
            </div>
          ))}
          {isLoading ? <p className="text-xs text-slate-500">Loading latest settings...</p> : null}
        </div>
      </section>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-4 flex items-center text-lg font-semibold text-slate-900">
          <IconQuestionMark size={20} className="mr-2 inline-block" />
          Frequently Asked Questions
        </h2>
        <div className="space-y-2">
          {faqs.map((faq) => (
            <details key={faq.question} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer list-none font-semibold text-slate-900">
                {faq.question}
              </summary>
              <p className="mt-2 text-sm text-slate-600">{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 text-lg font-semibold text-slate-900">Need More Help?</h2>
        <p className="text-sm text-slate-600">
          If you have additional questions about the financial aid process, please contact the admissions office.
        </p>
      </section>
    </section>
  );
}
